import { hasFinePointer, prefersReducedMotion } from '../../ui/motion'

/**
 * Light that follows the pointer across the surface under it.
 *
 * One listener for the whole document rather than one per card: it finds the
 * surface under the pointer, writes the pointer's position on it as `--px` and
 * `--py`, and marks it `data-lit`. The stylesheet decides what that light looks
 * like (ambience.css): a catch of light on the edge of a panel, and on the
 * surfaces that can be clicked, a soft spot under the hand that says so.
 *
 * Throttled to one write per animation frame, and installed only where a
 * hovering, precise pointer exists and the reader has not asked for reduced
 * motion. Elsewhere it is a no-op and the surfaces are simply unlit.
 */
const SURFACES = [
  '.ui-card',
  '.rental-door',
  '.rental-path-card',
  '.size-preset',
  '.image-preset',
  '.runway-choice',
  '.landing-preview-frame',
  '.landing-part',
  '.entry-card',
  '.overview-surface',
  '.instance-table-wrap',
].join(', ')

export function installPointerLight(): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => undefined
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)')
  let stop = installActivePointerLight()
  const update = () => {
    stop()
    stop = installActivePointerLight()
  }
  reduced.addEventListener('change', update)
  fine.addEventListener('change', update)
  return () => {
    reduced.removeEventListener('change', update)
    fine.removeEventListener('change', update)
    stop()
  }
}

function installActivePointerLight(): () => void {
  if (typeof window === 'undefined' || !hasFinePointer() || prefersReducedMotion()) return () => undefined

  let frame = 0
  let last: PointerEvent | null = null
  let lit: HTMLElement | null = null

  const unlight = () => {
    lit?.removeAttribute('data-lit')
    lit = null
  }

  const apply = () => {
    frame = 0
    const event = last
    if (!event) return
    const target = event.target instanceof Element ? event.target.closest<HTMLElement>(SURFACES) : null
    if (lit && lit !== target) unlight()
    if (!target) return
    const rect = target.getBoundingClientRect()
    target.style.setProperty('--px', `${Math.round(event.clientX - rect.left)}px`)
    target.style.setProperty('--py', `${Math.round(event.clientY - rect.top)}px`)
    if (lit !== target) {
      target.setAttribute('data-lit', '')
      lit = target
    }
  }

  const move = (event: PointerEvent) => {
    if (event.pointerType === 'touch') return
    last = event
    if (!frame) frame = window.requestAnimationFrame(apply)
  }

  window.addEventListener('pointermove', move, { passive: true })
  document.documentElement.addEventListener('pointerleave', unlight)
  window.addEventListener('blur', unlight)
  return () => {
    if (frame) window.cancelAnimationFrame(frame)
    window.removeEventListener('pointermove', move)
    document.documentElement.removeEventListener('pointerleave', unlight)
    window.removeEventListener('blur', unlight)
    unlight()
  }
}
